import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Grid,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import palette from 'src/theme/palette';

const FieldRow = ({ label, value, fullWidth = false }) => (
  <Grid item xs={fullWidth ? 12 : 6}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 1.5,
        borderRadius: 1,
        bgcolor: '#fafafa',
        border: '1px solid #eee',
        height: '100%'
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontFamily: 'poppinsSemiBold',
          color: palette.primary.main,
          mb: 0.5
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontFamily: 'poppinsMedium',
          color: palette.primary.titledark,
          wordBreak: 'break-word'
        }}
      >
        {value || 'N/A'}
      </Typography>
    </Box>
  </Grid>
);

const Section = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      mb: 3,
      borderRadius: 2,
      border: '1px solid #eee',
      bgcolor: '#fff'
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2
      }}
    >
      <Box
        sx={{
          width: 4,
          height: 20,
          bgcolor: palette.primary.main,
          borderRadius: 1,
          mr: 1.5
        }}
      />
      <Typography
        sx={{
          fontSize: 16,
          fontFamily: 'poppinsBold',
          color: palette.primary.main
        }}
      >
        {title}
      </Typography>
    </Box>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Paper>
);

const TaskDetailDialog = ({ open, onClose, task }) => {
  if (!task) return null;
  const user = JSON.parse(localStorage.getItem('user'));
  const role_id = user?.role?.id;
  const isLimitedRole = [5, 6, 7].includes(role_id);

  // SUPER ADMIN 1
  // ORGANIZATION 2
  // ADMIN 3
  // PROJECT MANAGER 4
  // SUB CONTRACTOR 5
  // CLIENT 6
  // NEXUS 7

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: palette.primary.main,
          height: 56,
          px: 3
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsBold',
            color: palette.primary.white
          }}
        >
          Task Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: '#fff' }} />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ bgcolor: '#f9f9f9', px: 3, py: 2 }}>
        {/* Basic Information */}
        <Section title="Basic Information">
          <FieldRow label="Code" value={task?.code} />
          <FieldRow label="Name" value={task?.name} />
          <FieldRow label="Age" value={task?.age} />
          <FieldRow label="Condition" value={task?.condition_name} />
          <FieldRow label="Unit" value={task?.unit_name} />
          <FieldRow label="Coverage" value={task?.coverage} />
          <FieldRow label="Description" value={task?.description} fullWidth />
        </Section>

        {/* Cost Details */}
        <Section title="Cost Details">
          <FieldRow label="Quantity" value={task?.qty} />
          {!isLimitedRole && (
            <>
              <FieldRow label="Item Amount ($)" value={task?.item_amount} />
              <FieldRow label="Reported Cost ($)" value={task?.reported_cost} />
              <FieldRow label="Unit Cost ($)" value={task?.unit_cost} />
            </>
          )}
        </Section>

        {/* Labor Details */}
        {!isLimitedRole && (
          <Section title="Labor Details">
            <FieldRow label="Worker's Wage ($)" value={task?.worker_wage} />
            <FieldRow label="Labor Burden ($)" value={task?.labor_burden} />
            <FieldRow label="Labor Overhead" value={task?.labor_overhead} />
            <FieldRow label="Labor Minimum" value={task?.labor_minimum} />
            <FieldRow label="Material ($)" value={task?.material} />
            <FieldRow label="Equipment ($)" value={task?.equipment} />
            <FieldRow label="Market Condition ($)" value={task?.market_condition} />
            <FieldRow label="Sales Tax ($)" value={task?.sales_tax} />
          </Section>
        )}

        {/* Financial Details */}
        {!isLimitedRole && (
          <Section title="Financial Details">
            <FieldRow label="RCV ($)" value={task?.rcv} />
            <FieldRow label="Life" value={task?.life} />
            <FieldRow label="Depreciation" value={task?.depreciation_type_name} />
            <FieldRow label="Depreciation Amount" value={task?.depreciation_amount} />
            <FieldRow label="Recoverable" value={task?.recoverable == 1 ? 'Yes' : 'No'} />
            <FieldRow label="ACV ($)" value={task?.acv} />
            <FieldRow label="Activity" value={task?.activity_name} />
            <FieldRow label="Tax" value={task?.tax == 1 ? 'Yes' : 'No'} />
            <FieldRow label="Replace" value={task?.replace == 1 ? 'Yes' : 'No'} />
          </Section>
        )}

        {/* Additional Information */}
        <Section title="Additional Information">
          <FieldRow label="Category" value={task?.category_name} />
          <FieldRow label="Selection" value={task?.sel} />
          {isLimitedRole ? (
            <>
              <FieldRow label="Date" value={task?.date} />
              <FieldRow label="Notes" value={task?.note} fullWidth />
            </>
          ) : (
            <>
              <FieldRow label="Owner" value={task?.owner} />
              <FieldRow label="Original Vendor" value={task?.original_vendor} />
              <FieldRow label="Source Name" value={task?.source_name} />
              <FieldRow label="Date" value={task?.date} />
              <FieldRow label="ADJ Source" value={task?.adj_source} />
              <FieldRow label="Notes" value={task?.note} fullWidth />
            </>
          )}
        </Section>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailDialog;
